import { ArrowPathRoundedSquareIcon, ArrowsRightLeftIcon, CheckIcon, ChevronDownIcon, ChevronUpIcon, EyeIcon, EyeSlashIcon, PlusIcon, SunIcon, XMarkIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { forwardRef, memo } from 'react';
import type { ObjectGroup, ObjectModifier } from 'shared-types';
import { Popover } from '../../../components/Popover';
import { ListItem } from '../../../components/ListItem';
import { T, useT } from '../../../translation/src';
import type { HeroIcon } from '../../../types';
import { addModifierChoice, applyMirrorModifier, removeRepeat, updateGroup, updateModifier, type ModifierChoice } from '../document/specOps';
import { useEditorState, useObjectEditor } from '../EditorContext';
import { FieldRow, PanelSection, Segmented } from '../panels/fields';
import { BindingField } from './BindingField';
import { IndexRepeatNote, LinearRepeatFields, RadialRepeatFields } from './RepeatFields';

/** A rounded corner (heroicons has none), shaped like a heroicon so menus take it too. */
const RoundCornerIcon: HeroIcon = forwardRef((props, ref) => (
  <svg ref={ref} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" {...props}>
    <path d="M5 19V13a8 8 0 0 1 8-8h6" />
  </svg>
));
RoundCornerIcon.displayName = 'RoundCornerIcon';

const ICONS: Record<ModifierChoice, HeroIcon> = { repeatLinear: ArrowPathRoundedSquareIcon, repeatRadial: SunIcon, mirror: ArrowsRightLeftIcon, roundCorners: RoundCornerIcon };

/**
 * "+ Add" of the stack: pick a type and it is added with sensible defaults. A
 * group has one repeat at most, so the repeat entries go once it has one.
 * `onPick` lets the shape inspector group the selection first.
 */
const AddModifierMenu_: React.FC<{ hasRepeat: boolean; onPick: (choice: ModifierChoice) => void }> = ({ hasRepeat, onPick }) => {
  const labels: Record<ModifierChoice, string> = {
    repeatLinear: useT('repeat in a row', { swc: true }),
    repeatRadial: useT('repeat around a point', { swc: true }),
    mirror: useT('mirror', { swc: true }),
    roundCorners: useT('round corners', { swc: true }),
  };
  const choices: ModifierChoice[] = [...(hasRepeat ? [] : (['repeatLinear', 'repeatRadial'] as const)), 'mirror', 'roundCorners'];
  return (
    <Popover
      anchor="bottom end"
      fitContent
      buttonChild={
        <span className="flex items-center gap-1 rounded-md border border-gray-300 px-2 py-0.5 text-xs transition-colors hover:bg-gray-100 dark:border-gray-600 dark:hover:bg-black">
          <PlusIcon className="h-3.5 w-3.5" />
          <T _str="add" swc />
        </span>
      }
    >
      {choices.map((choice) => (
        <ListItem key={choice} icon={ICONS[choice]} onClick={() => onPick(choice)}>
          {labels[choice]}
        </ListItem>
      ))}
    </Popover>
  );
};
export const AddModifierMenu = memo(AddModifierMenu_);

const IconButton: React.FC2<{ label: string; onClick: () => void; disabled?: boolean; danger?: boolean }> = ({ label, onClick, disabled, danger, children }) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    disabled={disabled}
    onClick={onClick}
    className={classNames('rounded p-0.5 transition-colors disabled:opacity-30', danger ? 'hover:bg-error-red-500/15 hover:text-error-red-500' : 'hover:bg-black/10 dark:hover:bg-white/10')}
  >
    {children}
  </button>
);

/** A stack card: icon, title and header buttons, then the settings. */
const Card: React.FC2<{ icon: HeroIcon; title: React.ReactNode; buttons: React.ReactNode; dimmed?: boolean }> = ({ icon: Icon, title, buttons, dimmed, children }) => (
  <div className={classNames('rounded-lg border border-gray-300 bg-white/60 dark:border-gray-600 dark:bg-white/5', dimmed && 'opacity-60')}>
    <div className="flex items-center gap-1.5 border-b border-gray-200 px-2 py-1.5 dark:border-gray-700">
      <Icon className="h-4 w-4 shrink-0 text-french-violet-600 dark:text-neon-green-300" />
      <span className="flex-1 truncate text-sm font-medium">{title}</span>
      {buttons}
    </div>
    <div className="flex flex-col gap-2 p-2">{children}</div>
  </div>
);

/** The repeat card: always first in the stack (the copies are made before the modifiers act on them). */
const RepeatCard: React.FC<{ group: ObjectGroup }> = ({ group }) => {
  const { store } = useObjectEditor();
  const remove = useT('remove', { swc: true });
  const repeat = group.repeat;
  if (!repeat) return null;
  const update = (next: NonNullable<ObjectGroup['repeat']>) => store.apply((s) => updateGroup(s, group.id, (g) => ({ ...g, repeat: next })));
  const title = repeat.kind === 'linear' ? <T _str="repeat in a row" swc /> : repeat.kind === 'radial' ? <T _str="repeat around a point" swc /> : <T _str="repeat by rule" swc />;
  return (
    <Card
      icon={repeat.kind === 'radial' ? SunIcon : ArrowPathRoundedSquareIcon}
      title={title}
      buttons={
        <IconButton label={remove} danger onClick={() => store.apply((s) => removeRepeat(s, group.id))}>
          <XMarkIcon className="h-4 w-4" />
        </IconButton>
      }
    >
      {repeat.kind === 'linear' && <LinearRepeatFields group={group} repeat={repeat} update={update} />}
      {repeat.kind === 'radial' && <RadialRepeatFields repeat={repeat} update={update} />}
      {repeat.kind === 'index' && <IndexRepeatNote />}
    </Card>
  );
};

/** One modifier card: on/off, order, apply (mirror), remove, and its settings. */
const ModifierCard: React.FC<{ group: ObjectGroup; modifier: ObjectModifier; index: number; count: number }> = ({ group, modifier, index, count }) => {
  const { store } = useObjectEditor();
  const { spec } = useEditorState();
  const t = {
    hide: useT('turn off', { swc: true }),
    show: useT('turn on', { swc: true }),
    up: useT('move up', { swc: true }),
    down: useT('move down', { swc: true }),
    apply: useT('apply: make the mirrored copies real shapes', { swc: true }),
    remove: useT('remove', { swc: true }),
  };
  const on = modifier.enabled !== false;
  const set = (next: ObjectModifier | null, move = 0) => store.apply((s) => updateModifier(s, group.id, index, next, move));

  return (
    <Card
      icon={modifier.kind === 'mirror' ? ArrowsRightLeftIcon : RoundCornerIcon}
      title={modifier.kind === 'mirror' ? <T _str="mirror" swc /> : <T _str="round corners" swc />}
      dimmed={!on}
      buttons={
        <>
          <IconButton label={on ? t.hide : t.show} onClick={() => set({ ...modifier, enabled: !on })}>
            {on ? <EyeIcon className="h-4 w-4" /> : <EyeSlashIcon className="h-4 w-4" />}
          </IconButton>
          {count > 1 && (
            <>
              <IconButton label={t.up} disabled={index === 0} onClick={() => set(modifier, -1)}>
                <ChevronUpIcon className="h-4 w-4" />
              </IconButton>
              <IconButton label={t.down} disabled={index === count - 1} onClick={() => set(modifier, 1)}>
                <ChevronDownIcon className="h-4 w-4" />
              </IconButton>
            </>
          )}
          {modifier.kind === 'mirror' && (
            <IconButton label={t.apply} onClick={() => store.apply((s) => applyMirrorModifier(s, group.id, index))}>
              <CheckIcon className="h-4 w-4" />
            </IconButton>
          )}
          <IconButton label={t.remove} danger onClick={() => set(null)}>
            <XMarkIcon className="h-4 w-4" />
          </IconButton>
        </>
      }
    >
      {modifier.kind === 'mirror' ? (
        <Segmented
          value={modifier.axis}
          options={[
            { value: 'u', label: <T _str="left ↔ right" /> },
            { value: 'n', label: <T _str="back ↔ front" /> },
            { value: 'both', label: <T _str="both" /> },
          ]}
          onChange={(axis) => set({ ...modifier, axis })}
        />
      ) : (
        <FieldRow label={<T _str="radius" swc />}>
          <BindingField binding={modifier.radius} params={spec.params.filter((p) => p.kind === 'float' || p.kind === 'int')} unit="m" fallback={0.05} minValue={0} onChange={(radius) => radius && set({ ...modifier, radius })} />
        </FieldRow>
      )}
    </Card>
  );
};

/**
 * The group's modifier stack (Blender-style): its repeat first, then mirror /
 * round corners in order — effects on the live drawing that stay editable and
 * follow every resize. "+ Add" picks the type.
 */
const ModifierStack_: React.FC<{ group: ObjectGroup }> = ({ group }) => {
  const { store } = useObjectEditor();
  const modifiers = group.modifiers ?? [];
  return (
    <PanelSection title={<T _str="modifiers" />} actions={<AddModifierMenu hasRepeat={!!group.repeat} onPick={(choice) => store.apply((s) => addModifierChoice(s, group.id, choice))} />}>
      <RepeatCard group={group} />
      {modifiers.map((modifier, index) => (
        <ModifierCard key={index} group={group} modifier={modifier} index={index} count={modifiers.length} />
      ))}
    </PanelSection>
  );
};

export const ModifierStack = memo(ModifierStack_);
